import { useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';

type StaffRow = {
  id: number;
  userAccount: string;
  userNickName: string;
  role: string;
  status: string;
  financeAccess: number;
  createdAt: string;
};

const STATUS_LABEL: Record<string, string> = {
  active: '활성',
  pending: '승인 대기',
  rejected: '거절됨',
};

const STATUS_BADGE: Record<string, string> = {
  active: 'manage-badge--green',
  pending: 'manage-badge--amber',
  rejected: 'manage-badge--red',
};

const ROLE_LABEL: Record<string, string> = {
  super: '최종관리자',
  staff: '일반관리자',
};

export default function ManageStaffApproval({
  authChurch,
  requesterId,
}: {
  authChurch: string;
  requesterId: number;
}) {
  const [rows, setRows] = useState<StaffRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<number | null>(null);

  const fetchStaff = () => {
    setLoading(true);
    setError(null);
    axios
      .get(`${MainURL}/login/staff`, { params: { authChurch, requesterId } })
      .then((res) => setRows(res.data?.data ?? []))
      .catch((err) => {
        console.error('GET /login/staff', err);
        setError('담당자 목록을 불러오지 못했습니다.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChurch, requesterId]);

  const respond = (targetId: number, action: 'approve' | 'reject') => {
    setProcessingId(targetId);
    axios
      .post(`${MainURL}/login/${action}`, { authChurch, requesterId, targetId })
      .then(() => fetchStaff())
      .catch((err) => console.error(`POST /login/${action}`, err))
      .finally(() => setProcessingId(null));
  };

  const toggleFinanceAccess = (row: StaffRow) => {
    const enabled = !row.financeAccess;
    const message = enabled
      ? `${row.userNickName} 님에게 재정관리 권한을 줄까요? 재정 앱과 웹 재정관리에서 헌금·지출 내역을 보고 수정할 수 있게 됩니다.`
      : `${row.userNickName} 님의 재정관리 권한을 회수할까요?`;
    if (!window.confirm(message)) return;
    setProcessingId(row.id);
    axios
      .post(`${MainURL}/login/finance-access`, { authChurch, requesterId, targetId: row.id, enabled })
      .then(() => fetchStaff())
      .catch((err) => {
        console.error('POST /login/finance-access', err);
        alert('재정 권한을 변경하지 못했습니다.');
      })
      .finally(() => setProcessingId(null));
  };

  const pendingRows = rows.filter((row) => row.status === 'pending');
  const otherRows = rows.filter((row) => row.status !== 'pending');

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">담당자 승인관리</h2>
          <p className="manage-panel-desc">가입 신청한 일반관리자를 승인하거나 거절하고, 재정관리 권한을 부여합니다. 최종관리자만 접근할 수 있습니다.</p>
        </div>
      </div>

      {loading && <div className="manage-state">불러오는 중입니다...</div>}
      {!loading && error && <div className="manage-state manage-state--error">{error}</div>}

      {!loading && !error && (
        <>
          <section className="manage-card-block">
            <div className="manage-block-head">
              <h3 className="manage-block-title">승인 대기</h3>
              <span className="manage-block-count">{pendingRows.length}</span>
            </div>
            <div className="manage-table-wrap">
              <table className="manage-table">
                <thead>
                  <tr>
                    <th>아이디</th>
                    <th>닉네임</th>
                    <th style={{ width: 160 }}>가입일</th>
                    <th style={{ width: 160 }}>처리</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingRows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="manage-table-empty">승인 대기 중인 가입 신청이 없습니다.</td>
                    </tr>
                  )}
                  {pendingRows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.userAccount}</td>
                      <td>{row.userNickName}</td>
                      <td>{row.createdAt?.slice(0, 10)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            type="button"
                            className="manage-btn manage-btn--primary manage-btn--sm"
                            disabled={processingId === row.id}
                            onClick={() => respond(row.id, 'approve')}
                          >
                            승인
                          </button>
                          <button
                            type="button"
                            className="manage-btn manage-btn--danger manage-btn--sm"
                            disabled={processingId === row.id}
                            onClick={() => respond(row.id, 'reject')}
                          >
                            거절
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="manage-card-block">
            <div className="manage-block-head">
              <h3 className="manage-block-title">전체 담당자</h3>
              <span className="manage-block-count">{otherRows.length}</span>
            </div>
            <div className="manage-table-wrap">
              <table className="manage-table">
                <thead>
                  <tr>
                    <th>아이디</th>
                    <th>닉네임</th>
                    <th style={{ width: 100 }}>권한</th>
                    <th style={{ width: 100 }}>상태</th>
                    <th style={{ width: 140 }}>재정 권한</th>
                    <th style={{ width: 160 }}>가입일</th>
                  </tr>
                </thead>
                <tbody>
                  {otherRows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="manage-table-empty">등록된 담당자가 없습니다.</td>
                    </tr>
                  )}
                  {otherRows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.userAccount}</td>
                      <td>{row.userNickName}</td>
                      <td>
                        <span className="manage-badge manage-badge--navy">{ROLE_LABEL[row.role] || row.role}</span>
                      </td>
                      <td>
                        <span className={`manage-badge ${STATUS_BADGE[row.status] || 'manage-badge--gray'}`}>
                          {STATUS_LABEL[row.status] || row.status}
                        </span>
                      </td>
                      <td>
                        {row.role === 'super' ? (
                          <span className="manage-badge manage-badge--green">항상 허용</span>
                        ) : row.status === 'active' ? (
                          <button
                            type="button"
                            className={`manage-btn manage-btn--sm${row.financeAccess ? ' manage-btn--danger' : ' manage-btn--primary'}`}
                            disabled={processingId === row.id}
                            onClick={() => toggleFinanceAccess(row)}
                          >
                            {row.financeAccess ? '권한 회수' : '권한 부여'}
                          </button>
                        ) : (
                          <span className="manage-badge manage-badge--gray">-</span>
                        )}
                      </td>
                      <td>{row.createdAt?.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
